import { Link, NavLink as RouterNavLink, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AppShell,
  Group,
  Title,
  Button,
  Menu,
  Avatar,
  Badge,
  Skeleton,
  Anchor,
} from "@mantine/core";
import { IconChevronDown, IconLogout } from "@tabler/icons-react";
import { useAuth } from "../auth/AuthContext";
import { getFreshness } from "../api/endpoints";
import { RawFreshness } from "../types/apiShapes";

const FRESHNESS_WARNING_HOURS = 48;

// Real shape of GET /api/meta/freshness (verified live — very different from
// the guessed `Freshness` type in src/types/index.ts, which describes
// per-dataset objects like `stats_dataset` that don't exist on the wire).
// The real payload has a flat `datasets` array (each entry named by
// `dataset`, e.g. "player_week_stats") plus `latest_week_loaded` /
// `current_nfl_week` objects. See src/types/apiShapes.ts for the adapter type.

function FreshnessBadge() {
  const { data, isLoading } = useQuery({
    queryKey: ["freshness"],
    queryFn: () => getFreshness() as unknown as Promise<RawFreshness>,
    // The badge is a "nice to have" indicator; a failure here should never
    // block or disrupt the rest of the shell.
    retry: 1,
  });

  if (isLoading) {
    return <Skeleton height={20} width={160} radius="sm" />;
  }

  // Handle the endpoint returning nothing (or an unexpected shape) gracefully.
  const statsEntry = data?.datasets?.find((d) => d.dataset === "player_week_stats");
  const week = data?.latest_week_loaded?.week;
  if (!data || !statsEntry || typeof week !== "number") {
    return null;
  }

  const ingestedAt = statsEntry.finished_at ? new Date(statsEntry.finished_at) : null;
  const ageHours = ingestedAt
    ? (Date.now() - ingestedAt.getTime()) / (1000 * 60 * 60)
    : null;
  const isStale = ageHours !== null && ageHours > FRESHNESS_WARNING_HOURS;

  return (
    <Badge
      color={isStale ? "yellow" : "green"}
      variant="light"
      data-testid="freshness-badge"
    >
      Stats current through Week {week}
    </Badge>
  );
}

export default function Layout() {
  const { user, loading, logout } = useAuth();

  return (
    <AppShell header={{ height: 60 }} padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="lg">
            <Title order={3}>Fantasy Stats Tracker</Title>
            <Group gap="md" component="nav" aria-label="Main navigation">
              <Anchor component={RouterNavLink} to="/players" underline="hover">
                Players
              </Anchor>
              <Anchor component={RouterNavLink} to="/teams" underline="hover">
                Teams
              </Anchor>
              <Anchor component={RouterNavLink} to="/scoring" underline="hover">
                Scoring
              </Anchor>
            </Group>
          </Group>

          <Group gap="md">
            <FreshnessBadge />
            {loading ? (
              <Skeleton height={32} width={100} radius="sm" />
            ) : user ? (
              <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                  <Button
                    variant="subtle"
                    aria-label={`Account menu for ${user.email}`}
                    rightSection={<IconChevronDown size={14} />}
                    leftSection={<Avatar size="sm" radius="xl" />}
                  >
                    {user.email}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconLogout size={14} />}
                    onClick={() => {
                      void logout();
                    }}
                  >
                    Logout
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ) : (
              <Group gap="xs">
                <Button component={Link} to="/login" variant="subtle">
                  Login
                </Button>
                <Button component={Link} to="/register" variant="filled">
                  Register
                </Button>
              </Group>
            )}
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
